import React from "react";
import { BookOutlined, DatabaseOutlined, SettingOutlined, DollarOutlined } from "@ant-design/icons";
import {
  LibraryConfiguration,
  UpliftsDataImport,
  MatterTemplateDataImport,
  DisbursementsManager,
} from "./library-content";
import { ReferenceDataConfiguration } from "./reference-data";
import { InstanceConfiguration, OrganisationSettingsContainer } from "./instance-settings";
import { ClientSettingsContainer } from "./client-settings";
import { GpmVisibilityContainer } from "./gpm-visibility";
import { AppSettingsContainer } from "./app-settings";
import { ReferenceData } from "./reference-data/reference-data";
import { Permission, usePermissionCheck } from "../../hooks";
import { HierarchySettingsContainer } from "./hierarchy/hierarchy-container";
import { AssumptionsDataImportWrapper } from "./library-content/assumptions-data-import-wrapper";
import { ExclusionsDataImportWrapper } from "./library-content/exclusions-data-import-wrapper";
import { TechnologyDataImportWrapper } from "./library-content/technology-data-import-wrapper";
import { PracticeAreaSettingsContainer } from "./practice-area/practice-area-settings-container";
import { TaxSettingsContainer } from "./tax-settings/tax-settings-container";
import { ReportsPage } from "./reports/reports-page";
import { DownloadReportsPage } from "./reports/download-reports-page";
import { IntegrationReportsPage } from "./reports/integration-reports-page";
import { ProposalDesignEditorPage } from "./output-builder/proposal-design-editor-page";
import { TextBlockEditorPage } from "./output-builder/text-block-editor-page";
import { ClientReportDesignEditorPage } from "./output-builder/client-report-design-editor-page";
import { RoiContainer } from "../roi/roi-container";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { TaxonomySettingsContainer } from "./taxonomy/taxonomy-config";
import { DynamicTagsPage } from "./output-builder/dynamic-tags-page";
import { useSettings } from "~/hooks/settings/use-settings";

export const ConfigurationMenuItems = () => {
  const { settings } = useSettings();
  const taxEnabled = settings?.taxEnabled;
  const isNoneIntegrated = usePermissionCheck([Permission.IMPORT_REFERENCE_DATA]);

  const hasConfigurationPermissions = usePermissionCheck([
    Permission.READ_CONFIG,
    Permission.UPDATE_CONFIG,
  ]);
  const hasTemplatePermissions = usePermissionCheck([Permission.MATTER_TEMPLATE_EDITOR]);
  const hasRoiPermissions = usePermissionCheck([Permission.BETA_ROI]);
  const taxonomy = useTaxonomyLabel();
  const configItems = [
    ...(isNoneIntegrated && hasConfigurationPermissions
      ? [
          {
            path: "reference-data",
            title: "Reference Data",
            element: <ReferenceDataConfiguration />,
            icon: <DatabaseOutlined />,
            children: [
              {
                path: "data-import",
                title: "Data Import",
                element: <ReferenceData />,
              },
            ],
          },
        ]
      : []),
    ...(hasTemplatePermissions
      ? [
          {
            path: "library-content",
            title: "Library Content",
            icon: <BookOutlined />,
            element: <LibraryConfiguration />,
            children: [
              {
                path: "matter-templates",
                title: `Manage ${taxonomy.Template}s`,
                element: <MatterTemplateDataImport />,
              },
              ...(hasConfigurationPermissions
                ? [
                    {
                      path: "disbursements",
                      title: "Disbursements",
                      element: <DisbursementsManager />,
                    },
                  ]
                : []),
              {
                path: "assumptions",
                title: "Assumptions",
                element: <AssumptionsDataImportWrapper />,
              },
              { path: "exclusions", title: "Exclusions", element: <ExclusionsDataImportWrapper /> },
              ...(hasConfigurationPermissions
                ? [{ path: "uplifts", title: "Uplifts", element: <UpliftsDataImport /> }]
                : []),
              {
                path: "technologies",
                title: "Technologies",
                element: <TechnologyDataImportWrapper />,
              },
            ],
          },
        ]
      : []),
    ...(hasConfigurationPermissions
      ? [configurationSettingsItems(taxonomy.PracticeArea, taxEnabled ?? false)]
      : []),
    ...(hasConfigurationPermissions
      ? [
          {
            path: "reports",
            icon: <BookOutlined />,
            title: "Reports",
            children: [
              {
                path: "",
                title: "Usage Reports",
                element: <ReportsPage />,
              },
              {
                path: "download",
                title: "Download Reports",
                element: <DownloadReportsPage />,
              },
              {
                path: "integration",
                title: "Integration Reports",
                element: <IntegrationReportsPage />,
              },
              ...(hasRoiPermissions
                ? [
                    {
                      path: "return-on-investment",
                      icon: <DollarOutlined />,
                      title: "Return On Investment",
                      element: <RoiContainer />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      path: "output-design-library",
      icon: <BookOutlined />,
      title: "Output Design Library",
      children: [
        {
          path: "proposal-design-editor",
          title: "Proposal Design Editor",
          element: <ProposalDesignEditorPage />,
        },
        {
          path: "text-block-editor",
          title: "Proposal Text Blocks",
          element: <TextBlockEditorPage />,
        },
        {
          path: "client-report-design-editor",
          title: "Client Report Design Library",
          element: <ClientReportDesignEditorPage />,
        },
        {
          path: "dynamic-tags",
          title: "Dynamic Tags",
          element: <DynamicTagsPage />,
        },
      ],
    },
  ];

  return configItems;
};

function configurationSettingsItems(PA_LABEL: string, taxEnabled: boolean) {
  const configSettings = {
    path: "settings",
    title: "Settings",
    icon: <SettingOutlined />,
    element: <InstanceConfiguration />,
    children: [
      {
        path: "organisation-settings",
        title: "Organisation Settings",
        element: <OrganisationSettingsContainer />,
      },
      ...(taxEnabled
        ? [
            {
              path: "tax-settings",
              title: "Tax Settings",
              element: <TaxSettingsContainer />,
            },
          ]
        : []),
      {
        path: "gpm-visibility",
        title: "GPM Visibility",
        element: <GpmVisibilityContainer />,
      },
      {
        path: "practice-area-settings",
        title: `${PA_LABEL} Settings`,
        element: <PracticeAreaSettingsContainer />,
      },
      {
        path: "client-settings",
        title: "Client Settings",
        element: <ClientSettingsContainer />,
      },
      {
        path: "hierarchy-settings",
        title: "Hierarchy",
        element: <HierarchySettingsContainer />,
      },
      {
        path: "taxonomy-settings",
        title: "Custom Labels",
        element: <TaxonomySettingsContainer />,
      },
      {
        path: "app-settings",
        title: "App Settings",
        element: <AppSettingsContainer />,
      },
    ],
  };

  return configSettings;
}
